/**
 * Phone Step Validator
 *
 * Targeted validation for Phone fields that disables
 * the Next button only when these fields are visible and invalid.
 *
 * Uses event delegation to handle dynamically rendered form steps.
 * Now includes error message display below invalid phone fields.
 */

import { FormatValidators } from './format-validators';
import { FieldType } from '../detection-patterns';
import { ensureLookup, getLookupState, onLookupSettled } from './phone-lookup-client';

/**
 * CSS class for phone error messages
 */
const PHONE_ERROR_MESSAGE_CLASS = 'phone-validation-error-message';

/** Class + text + injected-style id for the "validating…" spinner message. */
const PHONE_VALIDATING_MESSAGE_CLASS = 'phone-validating-message';
const PHONE_VALIDATING_STYLE_ID = 'phone-validating-styles';
const VALIDATING_PHONE_MESSAGE = 'Validating your phone number…';

/**
 * Phone field names to validate
 */
const PHONE_FIELD_NAMES = ['phone', 'phone_number', 'mobile', 'coapp_phone', 'cell', 'cell_phone'];

/**
 * Next button selector
 */
const NEXT_BUTTON_SELECTOR = 'input[data-form="next-btn"]';

/**
 * Shown when a well-formatted number fails the real-line check (Twilio
 * line_type_intelligence) — i.e. it's a working number but not the kind we
 * accept. Plain-English: what we need vs what we can't take.
 */
const UNACCEPTED_PHONE_MESSAGE =
  `Please enter a real mobile or home phone number where we can reach you. ` +
  `We can't accept internet or app-based numbers (like Google Voice or TextNow), ` +
  `temporary or "burner" numbers, or toll-free numbers (e.g. 1-800).`;

/**
 * Inject the spinner styles once. Grey text + blue accent to match the
 * processing page. Self-contained (no dependency on external CSS classes).
 */
let validatingStylesInjected = false;
function ensureValidatingStyles(): void {
  if (validatingStylesInjected || document.getElementById(PHONE_VALIDATING_STYLE_ID)) {
    validatingStylesInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = PHONE_VALIDATING_STYLE_ID;
  style.textContent = `
    .${PHONE_VALIDATING_MESSAGE_CLASS} {
      display: flex; align-items: center;
      color: #666; font-size: 14px; font-weight: 500; margin-top: 4px;
    }
    .phone-validating-spinner {
      display: inline-block; width: 14px; height: 14px; margin-right: 8px;
      border: 2px solid rgba(0,0,0,0.15); border-top-color: #3182ce;
      border-radius: 50%; animation: phone-validating-spin 0.7s linear infinite;
      flex: 0 0 auto;
    }
    @keyframes phone-validating-spin { to { transform: rotate(360deg); } }
  `;
  document.head.appendChild(style);
  validatingStylesInjected = true;
}

/**
 * Phone Step Validator class - uses event delegation for multi-step forms
 */
export class PhoneStepValidator {
  private isInitialized = false;
  private validationState: Map<string, boolean> = new Map();
  private lastButtonState: boolean | null = null;
  private lookupListenerBound = false;

  /**
   * Initialize the Phone step validator with event delegation
   */
  public initialize(): void {
    if (this.isInitialized) {
      console.log('📱 Phone Step Validator already initialized');
      return;
    }

    console.log('📱 Phone Step Validator initializing with event delegation...');

    // Use event delegation - listen on document for all input events
    document.addEventListener('input', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isPhoneField(target)) {
        console.log(`📱 Input event on Phone field: ${target.getAttribute('data-name') || target.name}`);
        this.handlePhoneInput(target);
      }
    });

    document.addEventListener('blur', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isPhoneField(target)) {
        console.log(`📱 Blur event on Phone field: ${target.getAttribute('data-name') || target.name}`);
        this.handlePhoneInput(target);
      }
    }, true); // Use capture for blur

    document.addEventListener('focus', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isPhoneField(target)) {
        console.log(`📱 Focus event on Phone field: ${target.getAttribute('data-name') || target.name}`);
        this.handlePhoneInput(target);
      }
    }, true); // Use capture for focus
    
    // Add keyup to catch updates that might be missed by input (e.g. some masks)
    document.addEventListener('keyup', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isPhoneField(target)) {
         this.handlePhoneInput(target);
      }
    });

    // Add paste to catch paste events
    document.addEventListener('paste', (event) => {
      const target = event.target as HTMLInputElement;
      if (this.isPhoneField(target)) {
         // Small delay to let the paste actually update the value
         setTimeout(() => this.handlePhoneInput(target), 50);
      }
    });
    
    // Also listen for change events on checkboxes in the same step
    // This catches the case where a user checks a box which might enable the next button
    document.addEventListener('change', (event) => {
      const target = event.target as HTMLInputElement;
      if (target.type === 'checkbox') {
        // If a checkbox changed, re-validate any visible phone fields to ensure button state stays correct
        const visiblePhoneFields = this.findVisiblePhoneFields();
        if (visiblePhoneFields.length > 0) {
           console.log('📱 Checkbox changed, re-verifying phone validation state...');
           // Trigger validation update based on the first visible phone field
           this.updateNextButtonState(visiblePhoneFields[0]);
        }
      }
    });

    // When a phone lookup settles, re-evaluate the Next button + message for
    // any visible phone field (no new keystroke required).
    if (!this.lookupListenerBound) {
      this.lookupListenerBound = true;
      onLookupSettled(() => {
        const fields = this.findVisiblePhoneFields();
        if (fields.length > 0) this.updateNextButtonState(fields[0]);
      });
    }

    this.isInitialized = true;
    console.log('📱 Phone Step Validator initialized with event delegation');
  }

  /**
   * Check if an element is a Phone field
   */
  private isPhoneField(element: HTMLInputElement): boolean {
    if (!element || element.tagName !== 'INPUT') return false;

    const dataName = element.getAttribute('data-name');
    const name = element.name;
    const id = element.id;
    const type = element.type;

    // Check by name/ID
    if (PHONE_FIELD_NAMES.includes(dataName || '') ||
        PHONE_FIELD_NAMES.includes(name || '') ||
        PHONE_FIELD_NAMES.includes(id || '')) {
      return true;
    }
    
    // Check by type
    if (type === 'tel') return true;
    
    return false;
  }

  /**
   * Handle input on a Phone field
   */
  private handlePhoneInput(field: HTMLInputElement): void {
    const fieldId = field.getAttribute('data-name') || field.name || field.id;
    const value = field.value.trim();

    // Validate the field
    let isValid = false;
    let errorMessage = '';

    // Only consider valid if it has value AND passes strict validation
    if (value) {
      const result = FormatValidators.validatePhone(value);
      isValid = result.isValid;

      // Get the first error message if invalid
      if (!isValid && result.errors.length > 0) {
        errorMessage = result.errors[0];
      }
    }

    this.validationState.set(fieldId, isValid);

    // Twilio Lookup gate: once the format is valid, confirm it's a real line.
    if (isValid) {
      ensureLookup(value);
    }
    // console.log(`📱 Phone "${fieldId}" value="${value}" → ${isValid ? 'VALID' : 'INVALID'}`);

    // Format-level message only. The lookup gate (updateNextButtonState) owns
    // the message for a format-valid number (validating spinner / unaccepted),
    // so we don't clear-then-readd and restart the spinner on every keystroke.
    if (!isValid && value) {
      this.showErrorMessage(field, errorMessage || 'Please enter a valid phone number');
    } else if (!value) {
      this.clearErrorMessage(field);
    }

    // Update button state
    this.updateNextButtonState(field);
  }

  /**
   * Check if a field is visible
   */
  private isFieldVisible(field: HTMLElement): boolean {
    if (!field || !document.body.contains(field)) return false;

    const rect = field.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const style = window.getComputedStyle(field);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    if (field.offsetParent === null && style.position !== 'fixed') return false;

    return true;
  }

  /**
   * Find all visible Phone fields on the page
   */
  private findVisiblePhoneFields(): HTMLInputElement[] {
    // First find by known names
    const fields: HTMLInputElement[] = [];

    for (const fieldName of PHONE_FIELD_NAMES) {
      // Try data-name, name, and id
      const selectors = [
        `input[data-name="${fieldName}"]`,
        `input[name="${fieldName}"]`,
        `input#${fieldName}`
      ];

      for (const selector of selectors) {
        const field = document.querySelector(selector) as HTMLInputElement;
        if (field && this.isFieldVisible(field) && !fields.includes(field)) {
          fields.push(field);
          // Don't break here, there might be multiple (e.g. main applicant + co-applicant)
        }
      }
    }
    
    // Also find any input[type="tel"]
    const telInputs = document.querySelectorAll('input[type="tel"]');
    telInputs.forEach((input) => {
        const field = input as HTMLInputElement;
        if (this.isFieldVisible(field) && !fields.includes(field)) {
            fields.push(field);
        }
    });

    return fields;
  }

  /**
   * Find the Next button near a Phone field
   */
  private findNextButton(phoneField: HTMLInputElement): HTMLInputElement | null {
    // Walk up to find a container with the Next button
    let container: HTMLElement | null = phoneField.closest('form') ||
                                          phoneField.closest('[data-form-step]') ||
                                          phoneField.closest('.w-form');

    if (!container) {
      container = phoneField.parentElement;
      for (let i = 0; i < 10 && container; i++) {
        if (container.querySelector(NEXT_BUTTON_SELECTOR)) break;
        container = container.parentElement;
      }
    }

    if (!container) return null;

    const button = container.querySelector(NEXT_BUTTON_SELECTOR) as HTMLInputElement;
    return button && this.isFieldVisible(button) ? button : null;
  }

  /**
   * Update the Next button state
   */
  private updateNextButtonState(triggeringField: HTMLInputElement): void {
    // Find all visible Phone fields
    const visiblePhoneFields = this.findVisiblePhoneFields();

    if (visiblePhoneFields.length === 0) {
      // If no phone fields are visible, this validator shouldn't block anything
      return;
    }

    // Find the Next button
    const nextButton = this.findNextButton(triggeringField);
    if (!nextButton) {
      return;
    }

    // Check if any visible Phone field is invalid
    let hasInvalidField = false;
    for (const field of visiblePhoneFields) {
      const fieldId = field.getAttribute('data-name') || field.name || field.id;
      const value = field.value.trim();

      // Re-validate to ensure current state
      let isValid = false;
      let errorMessage = '';
      if (value) {
        const result = FormatValidators.validatePhone(value);
        isValid = result.isValid;
        if (!isValid && result.errors.length > 0) {
          errorMessage = result.errors[0];
        }
      }

      console.log(`📱 Field "${fieldId}" value="${value}" valid=${isValid}`);

      // Message + Twilio lookup gate. A format-valid number must also be a
      // real line; the gate OWNS the message for format-valid fields so the
      // validating spinner persists smoothly (no clear-then-readd flicker).
      if (isValid) {
        const ls = getLookupState(value);
        if (ls === 'invalid') {
          isValid = false;
          this.showErrorMessage(field, UNACCEPTED_PHONE_MESSAGE);
        } else if (ls === 'valid' || ls === 'error') {
          this.clearErrorMessage(field); // real line (or fail-open) → no message
        } else {
          // 'pending'/'none' → gate while we confirm + show the spinner so the
          // user (esp. on mobile) knows we're checking.
          ensureLookup(value);
          isValid = false;
          this.showValidatingMessage(field);
        }
      } else if (value) {
        this.showErrorMessage(field, errorMessage || 'Please enter a valid phone number');
      } else {
        this.clearErrorMessage(field);
      }

      if (!isValid) {
        hasInvalidField = true;
      }
    }

    // Also check if required checkboxes are checked
    const hasUncheckedRequiredCheckbox = this.hasUncheckedRequiredCheckboxes(triggeringField);
    if (hasUncheckedRequiredCheckbox) {
      console.log('📱 Required checkbox not checked');
      hasInvalidField = true;
    }

    // Apply state immediately
    if (hasInvalidField) {
      console.log('📱 DISABLING Next button (immediate) due to invalid phone or unchecked checkbox');
      this.disableNextButton(nextButton);
    } else {
      console.log('📱 Phone is valid and checkboxes checked, releasing block on Next button');
      this.enableNextButton(nextButton);
    }

    // Also apply after a short delay to override Webflow's scripts or checkbox logic
    const btn = nextButton; // Capture reference
    const shouldDisable = hasInvalidField;
    
    // Multiple delays to fight race conditions with other scripts
    [50, 200, 500].forEach(delay => {
        setTimeout(() => {
            if (shouldDisable) {
                console.log(`📱 DISABLING Next button (delayed ${delay}ms)`);
                this.disableNextButton(btn);
            } else {
                // FORCE ENABLE in the delay loop too
                // This ensures that if Webflow or another script disabled it momentarily
                // (e.g. during input processing), we force it back to enabled if valid.
                // This fixes the issue where user has to click away to enable.
                this.enableNextButton(btn);
            }
        }, delay);
    });

    this.lastButtonState = hasInvalidField;
  }

  /**
   * Check if there are any unchecked required checkboxes in the same form step
   */
  private hasUncheckedRequiredCheckboxes(triggeringField: HTMLInputElement): boolean {
    // Find the form step container
    const container = triggeringField.closest('[data-slide-index], .w-slide, [class*="step"], form');
    if (!container) return false;

    // Find all required checkboxes in this container
    const requiredCheckboxes = container.querySelectorAll(
      'input[type="checkbox"][required], input[type="checkbox"][data-required="true"]'
    ) as NodeListOf<HTMLInputElement>;

    // Check if any required checkbox is not checked
    for (const checkbox of requiredCheckboxes) {
      if (!checkbox.checked && this.isFieldVisible(checkbox)) {
        console.log(`📱 Required checkbox not checked: ${checkbox.name || checkbox.id || 'unnamed'}`);
        return true;
      }
    }

    return false;
  }

  /**
   * Disable the Next button using Webflow's pattern
   */
  private disableNextButton(button: HTMLInputElement): void {
    button.classList.add('disabled');
    button.style.opacity = '0.4';
    button.style.pointerEvents = 'none';
  }

  /**
   * Enable the Next button using Webflow's pattern
   */
  private enableNextButton(button: HTMLInputElement): void {
    button.classList.remove('disabled');
    button.style.opacity = '1';
    button.style.pointerEvents = 'auto';
  }

  /**
   * Show error message below a phone field
   */
  private showErrorMessage(field: HTMLInputElement, errorMessage: string): void {
    // Clear any existing error message first
    this.clearErrorMessage(field);

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = PHONE_ERROR_MESSAGE_CLASS;
    errorElement.style.cssText = `
      color: #E53E3E;
      font-size: 14px;
      margin-top: 4px;
      display: block;
      font-weight: 500;
    `;
    errorElement.textContent = errorMessage;

    // Insert after the field (or after the field's wrapper if it has one)
    const wrapper = field.parentElement?.classList.contains('field-with-status-icon')
      ? field.parentElement
      : field;
    wrapper.parentNode?.insertBefore(errorElement, wrapper.nextSibling);
  }

  /**
   * Clear error message for a phone field
   */
  private clearErrorMessage(field: HTMLInputElement): void {
    // Clears BOTH the red error message and the "validating…" spinner message.
    const wrapper = field.parentElement?.classList.contains('field-with-status-icon')
      ? field.parentElement
      : field;
    const classes = [PHONE_ERROR_MESSAGE_CLASS, PHONE_VALIDATING_MESSAGE_CLASS];

    const nextSibling = wrapper.nextSibling;
    if (
      nextSibling instanceof HTMLElement &&
      classes.some((c) => nextSibling.classList.contains(c))
    ) {
      nextSibling.remove();
    }

    // Also clear any in the parent (fallback)
    const parent = wrapper.parentElement;
    if (parent) {
      for (const c of classes) {
        parent.querySelectorAll(`.${c}`).forEach((el) => el.remove());
      }
    }
  }

  /**
   * Show a "Validating your phone number…" message with a spinner in the same
   * slot as the error message, while the Twilio lookup is in flight. Important
   * on mobile so users know something is happening during the ~1s check.
   */
  private showValidatingMessage(field: HTMLInputElement): void {
    const wrapper = field.parentElement?.classList.contains('field-with-status-icon')
      ? field.parentElement
      : field;

    // Already showing it? Leave it (avoid flicker on repeated updates).
    const existing = wrapper.parentElement?.querySelector(`.${PHONE_VALIDATING_MESSAGE_CLASS}`);
    if (existing) return;

    this.clearErrorMessage(field);
    ensureValidatingStyles();

    const el = document.createElement('div');
    el.className = PHONE_VALIDATING_MESSAGE_CLASS;
    el.innerHTML =
      `<span class="phone-validating-spinner" aria-hidden="true"></span>` +
      `<span></span>`;
    const textSpan = el.lastElementChild as HTMLElement;
    textSpan.textContent = VALIDATING_PHONE_MESSAGE;

    wrapper.parentNode?.insertBefore(el, wrapper.nextSibling);
  }
}

/**
 * Singleton instance
 */
let phoneStepValidatorInstance: PhoneStepValidator | null = null;

/**
 * Get or create the Phone step validator instance
 */
export function getPhoneStepValidator(): PhoneStepValidator {
  if (!phoneStepValidatorInstance) {
    phoneStepValidatorInstance = new PhoneStepValidator();
  }
  return phoneStepValidatorInstance;
}

/**
 * Initialize the Phone step validator
 */
export function initializePhoneStepValidator(): void {
  const validator = getPhoneStepValidator();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => validator.initialize());
  } else {
    validator.initialize();
  }
}
